import $, { Cash } from 'cash-dom'

export interface ShareOptions {
  url: string
  title?: string
  text?: string
  updateUrls?: boolean
}

export class ShareSheet {
  container: Cash
  webShareButton: Cash = $()
  copyLinkButton: Cash = $()
  copySuccessMessage: Cash = $()
  shareOptions: ShareOptions

  constructor(container: Cash | HTMLElement, options: ShareOptions) {
    this.container = $(container)
    this.shareOptions = {
      title: options.title ?? 'Share',
      text: options.text ?? 'Check out Pura with my referral link',
      url: options.url,
    }
    this.shareOptions['updateUrls'] = options.updateUrls

    this.initShareSheet()
  }

  initShareSheet(): void {
    this.webShareButton = this.container.find('#web-share-button')
    this.copyLinkButton = this.container.find('[data-share="copy"]')
    this.copySuccessMessage = this.container.find('[data-copy-success]')

    if (typeof navigator.share === 'function') {
      this.webShareButton.removeClass('hidden')
    }

    this.copyLinkButton.attr('data-copy-url', this.shareOptions.url)
    this.copyLinkButton.on('click', (e: Event) => this.handleCopyLink(e))

    this.webShareButton.attr('data-share-url', this.shareOptions.url)
    this.webShareButton.attr('data-share-title', this.shareOptions.title ?? '')
    this.webShareButton.attr('data-share-text', this.shareOptions.text ?? '')
    this.webShareButton.on('click', (e: Event) => this.handleWebShare(e))

    if (this.shareOptions.updateUrls) {
      const urlToEncode = this.shareOptions.url
      const textToEncode = this.shareOptions.text ?? ''
      const titleToEncode = this.shareOptions.title ?? ''

      const encodedUrl = encodeURIComponent(urlToEncode)
      const encodedText = encodeURIComponent(textToEncode)
      const encodedTitle = encodeURIComponent(titleToEncode)

      this.container
        .find('[data-share="sms"]')
        .attr('href', `sms:?&body=${encodedText}%3A%20${encodedUrl}`)

      this.container
        .find('[data-share="facebook"]')
        .attr(
          'href',
          `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`
        )

      this.container
        .find('[data-share="email"]')
        .attr(
          'href',
          `mailto:?subject=${encodedTitle}&body=${encodedText}%3A%20${encodedUrl}`
        )
    }
  }

  handleWebShare(e: Event): void {
    e.preventDefault()
    const button = e.currentTarget as HTMLElement
    const $button = $(button)

    const shareData = {
      title: $button.attr('data-share-title') ?? this.shareOptions.title,
      text: $button.attr('data-share-text') ?? this.shareOptions.text,
      url: $button.attr('data-share-url') ?? this.shareOptions.url,
    }

    if (typeof navigator.share === 'function') {
      navigator.share(shareData).catch((err) => {
        console.error('Error sharing:', err)
      })
    }
  }

  handleCopyLink(e: Event): void {
    e.preventDefault()
    const button = e.currentTarget as HTMLElement
    const $button = $(button)
    const url = $button.attr('data-copy-url') ?? this.shareOptions.url

    if (url) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          this.copySuccessMessage.removeClass('hidden')

          setTimeout(() => {
            this.copySuccessMessage.addClass('hidden')
          }, 2000)
        })
        .catch((err) => {
          console.error('Could not copy text:', err)
        })
    }
  }

  updateShareOptions(options: Partial<ShareOptions>): void {
    this.shareOptions = {
      ...this.shareOptions,
      ...options,
    }
    this.initShareSheet()
  }
}

$(document).ready(() => {
  $('[data-share-sheet-container]').each((_, el) => {
    const container = $(el)

    if (container.is('[data-referral-share-container]')) {
      return
    }

    const url = container.attr('data-share-url') ?? window.location.href
    const title = container.attr('data-share-title') ?? ''
    const text = container.attr('data-share-text') ?? ''

    const updateUrls = container.attr('data-update-urls') === 'true'

    const _shareSheet = new ShareSheet(container, {
      url,
      title,
      text,
      updateUrls,
    })
  })
})
